import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AgentDesktopState } from '@agent/agent-interface/agent-desktop-routing.module';
import { IAgentStatusDeprecatedService } from '@agent/core/agent/agent-status-deprecated.service';
import { IPositionDeprecatedService } from '@agent/core/agent/position-deprecated.service';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { IEpticaOutgoingFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IDirectoryContact } from '@agent/core/search/models/directory-contact';
import { ISearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ICustomerCardsDeprecatedService } from '@commons/customer/customer-cards-deprecated.service';
import { ChannelDeprecatedImpl as ChannelUtil } from '@commons/models/channel';
import { forEach, ILogService, IPromise, IQService, isDefined, isUndefined } from 'angular';
import { i18n } from 'i18next';
import { filter } from 'rxjs';

import {
  AgentStatus,
  BookingFacade,
  BookingStatus,
  ChanEnum,
  Channel,
  ChannelDirection,
  ChannelType,
  Civility,
  COMETD_CLICK_TO_MAIL,
  COMETD_CUSTOMERS_SPECIFIC_CLICK_TO_MAIL,
  ContactDto,
  CSR_FACADE_PREFIX,
  Customer,
  CustomerChangesData,
  CustomerData,
  CustomerFacade,
  CustomerService,
  Direction,
  MessagingService,
  MixableChannel,
  None,
  Option,
  ToastClass,
  ToasterFacade,
  UserUtil,
} from '@contact/upgrade';

import { IRecipient, RecipientSearchType } from './models/recipient';

const MODULE_NAME = 'outgoingMailDeprecatedService';
const NEW_CUSTOMER_ID = 'NewCustomerId';
export type OutgoingMailServiceMode = 'EDIT' | 'VIEW';
export const OPENED_FOR_CONTACT = 'CONTACT';

export interface IOutgoingMailDeprecatedFacade {
  /**
   * Is the header menu should be active
   * It should be active is and only if there is an interaction on another channel
   * @param {MixableChannel[]} channel blinding configuration used to test if the OutgoingMail menu may be active
   * @return {boolean}    true iif is the header menu should be active
   */
  isHeaderMenuActive(mixable: MixableChannel[]): boolean;

  /**
   * Does the user have the necessary rights the access the form for outgoing mail
   * Requires rights : send_mail and (free_mail or search_contact_by_id or search_in_directory)
   * @return {boolean}    true if the user have the necessary rights
   */
  canOpenOutgoingMail(): boolean;

  /**
   * Does the user have the necessary rights the access the form for outgoing mail we have the iasId of the mail target
   * Requires rights : send_mail
   */
  canOpenOutgoingMailWithIasId(): boolean;

  /**
   * Opens
   *  - The contact selection form if the uuid doesn't match any contact
   *  - The contact selection if the found contact has multiple adresses
   *  - The eptica "create request" form for the found contact
   * @return {IPromise<void>} The promise of that the eptica form is well loaded
   */
  openOutgoingMailForContactUuid(uuid: string, responseId?: string): IPromise<void>;

  /**
   * Opens the eptica "create request" form for the found contact
   * @return {IPromise<void>} The promise of that the eptica form is well loaded
   */
  openOutgoingMailForContactUuidAndIasId(uuid: string | null, iasId?: string | null, email?: string | null): IPromise<void>;

  /**
   * Opens the contact selection form
   * @return {IPromise<void>} The promise of that the eptica form is well loaded
   */
  openOutgoingMailSelection(): IPromise<void>;

  /**
   * returns true if a contact on mail outgoing already exists
   * @return {boolean} true if a contact on mail outgoing already exists
   */
  isMailOutgoingInProgress(): boolean;
}

export interface IOutgoingMailDeprecatedService extends IOutgoingMailDeprecatedFacade {
  initContact(responseId: string, autoOpen?: boolean): IPromise<boolean>;

  getRecipientTypes(): Array<RecipientSearchType>;

  getSelectedRecipientType(): RecipientSearchType;

  getSelectedContact(): IRecipient | undefined;

  setSelectedContact(contact: IRecipient | undefined): void;

  selectRecipientType(recipientType: RecipientSearchType): void;

  getEmailInput(): string;

  setEmailInput(emailInput: string): void;
  performSearchContacts(repositoryName?: string, currentPage?: number, itemsPerPage?: number): IPromise<number>;
  performSearchCorrespondants(currentPage: number, itemsPerPage: number, repositoryName: string): IPromise<number>;
  getMode(): OutgoingMailServiceMode;

  getcandidateContactList(): Array<IRecipient>;

  validateSelection(responseId: string, newUri: string, connectionId?: string, agentConnectionId?: string): IPromise<IEpticaOutgoingFrame>;

  getEpticaRequest(): IPromise<IEpticaOutgoingFrame>;
  searchContactById(currentPage: number, itemsPerPage: number, repositoryName: string): IPromise<number>;
  resetResults(): void;

  getSelectedCorrespondant(): IDirectoryContact | undefined;

  setSelectedCorrespondant(correspondant: any): void;

  getCandidatesCorrespondant(): IDirectoryContact[];

  setCandidateCorrespondant(correspondant: IDirectoryContact[]): void;

  getCorrespondantInput(): string;

  setCorrespondantInput(correspondant: string): void;
  getContactIdInput(): string;
  setContactIdInput(contactId: string): void;
  getLock(): boolean;

  isEpticaFrameLoaded(): boolean;

  isActionInProgress(): boolean;

  setActionInProgress(actionInProgress: boolean): void;

  isFakeUnitMailKnowContact(responseId: string): boolean;

  /**
   * Can the user send outgoing mails ?
   *
   * @returns {boolean} true if and only if he can
   */
  canOpenOutgoingMail(): boolean;

  cancelOutgoingMailContactSelection(): void;

  fetchContactsThatExistInEptica(partialName: string): IPromise<Array<any>>;

  linkRequestIdToContactEntry(responseId: string, esRequestId: number): IPromise<boolean>;

  getOpenedFromInteraction(contactId: string): string;

  deleteOpenedFromInteraction(contactId: string): void;

  isNewCustomer(contactId: string): boolean;
}

export class OutgoingMailDeprecatedService implements IOutgoingMailDeprecatedService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    '$i18next',
    'httpDeprecated',
    'toasterFacade',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'sessionDeprecatedService',
    'search',
    'customerCardsDeprecatedService',
    'messaging',
    'positionDeprecatedService',
    'agentStatusDeprecatedService',
    'contactFacade',
    'customerFacade',
    'agentDesktopRouter',
  ];

  public name = MODULE_NAME;

  private selectedRecipientType: RecipientSearchType = RecipientSearchType.BY_EMAIL;
  private emailInput: string;
  private mode: OutgoingMailServiceMode;
  private matchingContacts: Array<IRecipient>;
  private matchingDirectContacts: Array<IRecipient>;
  private selectedContact: IRecipient | undefined;
  private selectedDirectContact: IRecipient | undefined;
  private currentResponseId: string;
  private buffer: Array<IPromise<Array<any>>> = [];
  private iframeLoaded: boolean = false;
  private requestPromise: IPromise<IEpticaOutgoingFrame>;
  private correspondantInput: string;
  private contactIdInput: string;
  private candidateCorrespondants: IDirectoryContact[];
  private selectedCorrespondant: IDirectoryContact | undefined;
  private lock: boolean = false;
  private actionInProgress: boolean = false;
  private openedFromInteraction: { [key: string]: string } = {};

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $i18next: i18n,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly toasterFacade: ToasterFacade,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly searchService: ISearchService,
    private readonly customerCardsDeprecatedService: ICustomerCardsDeprecatedService,
    private readonly messaging: MessagingService,
    private readonly positionDeprecatedService: IPositionDeprecatedService,
    private readonly agentStatusDeprecatedService: IAgentStatusDeprecatedService,
    private readonly contactFacade: BookingFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly router: AgentDesktopRouter
  ) {
    this.messaging
      .forRemote()
      .listen$<any>(COMETD_CUSTOMERS_SPECIFIC_CLICK_TO_MAIL)
      .pipe(filter(dto => isDefined(dto)))
      .subscribe(dto => {
        if (isDefined(dto)) {
          let email = dto.mail;
          let handleClickToMail = (): void => {
            this.customerCardsDeprecatedService
              .getCustomerCardById(dto.customerId)
              .then(customer => {
                if (!customer) throw new Error();
                return customer;
              })
              .then(
                ({ customer }) =>
                  this.openOutgoingMailForContactUuidAndIasId(customer.id, this.getFirstIasCustomerid(customer).getOrElse(''), email),
                () =>
                  this.toasterFacade.alert({
                    message: this.$i18next.t('agent:toast.no_customer_card_error_mail', {
                      customer_id: dto.customerId,
                    }),
                  })
              );
          };
          if (this.agentStatusDeprecatedService.getCurrentAgentState() === AgentStatus.CONNECTED) {
            if (this.positionDeprecatedService.hasPosition()) {
              let position = this.positionDeprecatedService.position!;
              this.agentStatusDeprecatedService.availableOnPosition(position.id, position.type).then(() => {
                handleClickToMail();
              });
            } else {
              this.$log.warn('[unit outgoing mail]Ignore click to call due to state connected without position');
            }
          } else {
            handleClickToMail();
          }
        }
      });

    this.messaging
      .forRemote()
      .listen$(COMETD_CLICK_TO_MAIL)
      .subscribe((dto: any) => {
        if (isDefined(dto)) {
          this.$log.debug('ClickToMail with mail : ' + dto.mail);
          this.searchService.searchContactByEmail(dto.mail).then(contacts => {
            let iasId: string | null = null;
            let contactUuid: string | null = null;

            forEach(contacts, contact => {
              forEach(contact.addresses, address => {
                if (address.uri === dto.mail) {
                  iasId = address.externalId;
                  contactUuid = contact.uuid;
                }
              });
            });
            if (contacts.length === 1 && iasId && contactUuid) {
              this.openOutgoingMailForContactUuidAndIasId(contactUuid, iasId, dto.mail);
            } else if (contacts.length === 1 && contactUuid) {
              this.openOutgoingMailForContactUuid(contactUuid);
            } else {
              if (this.canSendFreeMail()) {
                this.openOutgoingMailForContactUuidAndIasId(null, null, dto.mail);
              }
            }
          });
        }
      });
    // TODO use store
    // contact state event
    this.contactDeprecatedService
      .contactState$()
      .pipe(
        filter(event => event.contact.channel.type === ChannelType.MAIL && event.contact.channel.direction === Direction.OUTGOING),
        filter(event => event.state === BookingStatus.CLOSED)
      )
      .subscribe(() => {
        this.router.navigateToTile([AgentDesktopState.BASKET]);
      });
  }

  initContact(responseId: string, autoOpen: boolean = true): IPromise<boolean> {
    if (responseId !== this.currentResponseId) {
      this.currentResponseId = responseId;
      return this.contactDeprecatedService.getContact(responseId).then(
        contact => {
          this.resetSelection();
          if (contact.customerId) {
            return this.loadContactByUuid(contact.customerId, contact.iasCustomerId, contact.phoneNumber).then(result => {
              // if contact.id is the one for dummy unit-outgoing-mail then try to find the one from contact.phoneNumber
              if (!result) {
                if (isEmailValid(contact.phoneNumber)) {
                  return this.loadContactByEmail(contact.phoneNumber, autoOpen);
                } else {
                  return this.$q.resolve(false);
                }
              } else {
                return this.$q.resolve(true);
              }
            });
          } else if (isEmailValid(contact.phoneNumber)) {
            return this.loadContactByEmail(contact.phoneNumber, autoOpen);
          }
          return this.$q.resolve(false);
        },
        () => this.$q.resolve(false)
      );
    }
    return this.$q.resolve(false);
  }

  getRecipientTypes(): Array<RecipientSearchType> {
    let recipientTypeArray: RecipientSearchType[] = [];

    if (this.canSendFreeMail()) {
      recipientTypeArray.push(RecipientSearchType.BY_EMAIL);
    }

    if (this.canSearchByContactId()) {
      recipientTypeArray.push(RecipientSearchType.BY_CONTACT_ID);
    }

    if (this.canSearchInDirectory()) {
      recipientTypeArray.push(RecipientSearchType.BY_DIRECTORY);
    }

    return recipientTypeArray;
  }

  getSelectedRecipientType(): RecipientSearchType {
    return this.selectedRecipientType;
  }

  selectRecipientType(recipientType: RecipientSearchType): void {
    this.selectedRecipientType = recipientType;
  }

  getEmailInput(): string {
    return this.emailInput;
  }

  setEmailInput(emailInput: string): void {
    this.emailInput = emailInput;
    this.selectedContact = undefined;
  }

  getMode(): OutgoingMailServiceMode {
    return this.mode;
  }

  getLock(): boolean {
    return this.lock;
  }

  getcandidateContactList(): Array<IRecipient> {
    return this.matchingContacts;
  }

  getSelectedContact(): IRecipient | undefined {
    return this.selectedContact;
  }

  setSelectedContact(contact: IRecipient | undefined): void {
    this.selectedContact = contact;
  }

  getSelectedCorrespondant(): IDirectoryContact | undefined {
    return this.selectedCorrespondant;
  }

  setSelectedCorrespondant(correspondant: IDirectoryContact): void {
    this.selectedCorrespondant = correspondant;
  }

  getCandidatesCorrespondant(): IDirectoryContact[] {
    return this.candidateCorrespondants;
  }

  setCandidateCorrespondant(correspondant: IDirectoryContact[]): void {
    this.candidateCorrespondants = correspondant;
  }

  getCorrespondantInput(): string {
    return this.correspondantInput;
  }

  setCorrespondantInput(correspondant: string): void {
    this.correspondantInput = correspondant;
  }

  getContactIdInput(): string {
    return this.contactIdInput;
  }

  setContactIdInput(contactId: string): void {
    this.contactIdInput = contactId;
  }

  getEpticaRequest(): IPromise<IEpticaOutgoingFrame> {
    return this.requestPromise;
  }

  isActionInProgress(): boolean {
    return this.actionInProgress;
  }

  setActionInProgress(actionInProgress: boolean): void {
    this.actionInProgress = actionInProgress;
  }

  canOpenOutgoingMail(): boolean {
    return (
      this.epticaDeprecatedService.getCurrentEpticaCsr().isDefined() &&
      this.canSendMail() &&
      (this.canSendFreeMail() || this.canSearchByContactId() || this.canSearchInDirectory())
    );
  }

  isMailOutgoingInProgress(): boolean {
    return (
      this.contactDeprecatedService
        .getContactsByChannelType(ChannelType.MAIL)
        .filter(contact => contact.channel.direction === ChannelDirection.OUTGOING).length > 0
    );
  }

  canOpenOutgoingMailWithIasId(): boolean {
    return this.epticaDeprecatedService.getCurrentEpticaCsr().isDefined() && this.canSendMail();
  }

  getOpenedFromInteraction(contactId: string): string {
    return Option.of(this.openedFromInteraction[contactId]).getOrElse('');
  }

  deleteOpenedFromInteraction(contactId: string): void {
    delete this.openedFromInteraction[contactId];
  }

  openOutgoingMailForContactUuid(uuid: string, sourceResponseId?: string): IPromise<void> {
    return this.httpDeprecated
      .asPromise()
      .withData({ customerId: '', contactUuid: uuid, outgoingMail: '', sourceResponseId: '', draftId: '' })
      .post(CSR_FACADE_PREFIX + '/openUnitMail')
      .then(
        () => {
          this.toasterFacade.notification({
            message: 'agent:toast.openOutgoingMail',
            cssClass: ToastClass.MAIL,
            duration: 2500,
          });
          if (sourceResponseId) {
            this.openedFromInteraction[uuid] = sourceResponseId;
          }
          return;
        },
        error => {
          this.$log.error(`[eptica] Failed to create an outgoing mail for contact uuid [${uuid}] : ${error}`);
          this.toasterFacade.alert({ message: 'agent:toast.openOutgoingMailFailed' });
        }
      );
  }

  openOutgoingMailForContactUuidAndIasId(uuid: string | null, iasId?: string | null, email?: string | null): IPromise<void> {
    return this.httpDeprecated
      .asPromise()
      .withData({
        customerId: iasId,
        contactUuid: uuid,
        outgoingMail: email,
        draftId: '',
      })
      .post(CSR_FACADE_PREFIX + '/openUnitMail')
      .then(
        () => {
          if (uuid) {
            this.openedFromInteraction[uuid] = OPENED_FOR_CONTACT;
          }
          this.toasterFacade.notification({
            message: 'agent:toast.openOutgoingMail',
            cssClass: ToastClass.MAIL,
            duration: 2500,
          });
          return;
        },
        error => {
          this.$log.error(`[eptica] Failed to create an outgoing mail for contact iasId [${iasId}] and uuid [${uuid}] : ${error}`);
          this.toasterFacade.alert({ message: 'agent:toast.openOutgoingMailFailed' });
        }
      );
  }

  openOutgoingMailSelection(): IPromise<void> {
    return this.httpDeprecated
      .asPromise()
      .withData({ customerId: '', contactUuid: '', outgoingMail: '', draftId: '' })
      .post(CSR_FACADE_PREFIX + '/openUnitMail')
      .then(
        () => {
          this.toasterFacade.notification({
            message: 'agent:toast.openOutgoingMail',
            cssClass: ToastClass.MAIL,
            duration: 2500,
          });
          return;
        },
        error => {
          this.$log.error(`[eptica] Failed to create an outgoing mail : ${error}`);
          this.toasterFacade.alert({ message: 'agent:toast.openOutgoingMailFailed' });
        }
      );
  }

  cancelOutgoingMailContactSelection(): void {
    this.router.navigateToTile();
    this.setEmailInput('');
  }

  performSearchContacts(repositoryName?: string, currentPage?: number, itemsPerPage?: number): IPromise<number> {
    this.resetResults();
    this.lock = true;
    switch (this.selectedRecipientType) {
      case RecipientSearchType.BY_EMAIL:
        if (this.emailInput && this.emailInput.length > 2) {
          let promise = this.searchService.searchContactByName(this.emailInput, repositoryName || 'default');
          return this.manageAutoCompleteQueue(promise)
            .then(rawContacts => {
              if (currentPage && itemsPerPage) {
                const fullContacts = this.buildRecipientList(rawContacts, this.emailInput);
                this.matchingContacts = fullContacts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                return fullContacts.length;
              } else {
                this.matchingContacts = this.buildRecipientList(rawContacts, this.emailInput);
                return 0;
              }
            })
            .finally(() => (this.lock = false));
        }
        return this.$q.resolve(0);
      case RecipientSearchType.BY_DIRECTORY:
        if (this.selectedCorrespondant != null) {
          let promise = this.searchService.searchContactByEmail(this.selectedCorrespondant.email);
          return this.manageAutoCompleteQueue(promise)
            .then(rawContacts => {
              if (currentPage && itemsPerPage) {
                let fullContacts = this.buildRecipientList(rawContacts);
                this.matchingContacts = fullContacts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                return fullContacts.length;
              } else {
                this.matchingContacts = this.buildRecipientList(rawContacts);
                return 0;
              }
            })
            .finally(() => (this.lock = false));
        }
        return this.$q.resolve(0);
      default:
        return this.$q.resolve(0);
    }
  }

  performSearchCorrespondants(currentPage: number, itemsPerPage: number, repositoryName: string): IPromise<number> {
    this.resetResults();
    if (this.correspondantInput && this.correspondantInput.length > 2) {
      return this.searchService.searchCorrespondant(this.correspondantInput).then(correspondants => {
        this.candidateCorrespondants = correspondants;
        if (correspondants.length > 0) {
          this.selectedCorrespondant = correspondants[0];
          this.performSearchContacts(repositoryName, currentPage, itemsPerPage).then(totalItems => totalItems);
        }
        return 0;
      });
    } else {
      this.candidateCorrespondants = [];
      this.selectedContact = undefined;
    }
    return this.$q.resolve(0);
  }

  searchContactById(currentPage: number, itemsPerPage: number, repositoryName: string): IPromise<number> {
    this.resetResults();
    return this.searchService
      .searchContactById(this.contactIdInput, repositoryName)
      .then(rawContacts => {
        let fullContacts = this.buildRecipientList(rawContacts);
        this.matchingContacts = fullContacts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
        return fullContacts.length;
      })
      .finally(() => (this.lock = false));
  }

  searchContactCorrepondant(contactIdInput: string, repositoryName: string): IPromise<void> {
    this.resetResults();
    return this.searchService
      .searchContactById(contactIdInput, repositoryName)
      .then(rawContacts => (this.matchingContacts = this.buildRecipientList(rawContacts)))
      .then(() => {
        return;
      });
  }

  resetResults(): void {
    if (this.selectedRecipientType === RecipientSearchType.DIRECT) {
      this.matchingContacts = this.matchingDirectContacts;
      if (this.matchingContacts.length > 0) {
        this.selectedContact = this.matchingContacts[0];
      } else {
        this.selectedContact = this.selectedDirectContact;
      }
    } else {
      this.matchingContacts = [];
      this.selectedContact = undefined;
    }
  }

  validateSelection(responseId: string, newUri: string, connectionId: string, agentConnectionId: string): IPromise<IEpticaOutgoingFrame> {
    this.mode = 'VIEW';
    return this.createContactIfNecessary(newUri).then(
      recipient => {
        this.selectedContact = recipient;
        this.updateContact();
        this.createContactEntry({
          contactConnection: connectionId,
          csrConnection: agentConnectionId,
          email: newUri,
          uuid: this.selectedContact?.uuid,
        });
        return this.loadEpticaRequest(responseId);
      },
      () => {
        this.mode = 'EDIT';
        return this.$q.reject();
      }
    );
  }

  updateContact(): void {
    this.contactDeprecatedService.getContact(this.currentResponseId).then(contact => {
      if (this.selectedContact) {
        this.contactFacade.update(contact.responseId, {
          iasCustomerId: this.selectedContact.id,
          phoneNumber: this.selectedContact.email,
          customerId: this.selectedContact.uuid,
          customerName: !!this.selectedContact.name && !!this.selectedContact.name.trim() ? this.selectedContact.name : UserUtil.UNKNOWN,
          dummy: false,
        });
        this.customerFacade.load(this.selectedContact.uuid);
      }
      this.customerCardsDeprecatedService.getCustomerCards(contact).then(([card]) => {
        if (!!card) {
          this.customerCardsDeprecatedService.linkCustomerCardToResponse(
            this.currentResponseId,
            this.selectedContact?.uuid || card.customer.id,
            this.selectedContact && this.selectedContact.email
          );
        }
      });
    });
  }

  isEpticaFrameLoaded(): boolean {
    return this.iframeLoaded;
  }

  fetchContactsThatExistInEptica(partialName: string): IPromise<Array<any>> {
    return this.httpDeprecated
      .asPromise()
      .get(`/getContactsByName/${partialName}`)
      .then(
        response => response.data,
        () => {
          this.$log.error('[outgoingMail] Error while retrieving contacts.');
          return this.$q.reject('[outgoingMail] Error while retrieving contacts.');
        }
      );
  }

  isHeaderMenuActive(mixable: MixableChannel[]): boolean {
    function isMixableWith(activeChannel: Channel, mixableChannels: MixableChannel[], expectedChannel: Channel): boolean {
      return (
        mixableChannels.filter((mChannel: MixableChannel) => {
          if (
            mChannel.channel.type === ChannelType.PHONE &&
            mChannel.channel.direction === ChannelDirection.INCOMING &&
            activeChannel.type === ChannelType.UNITARY_PHONE &&
            activeChannel.direction === ChannelDirection.OUTGOING
          ) {
            // UNITARY_PHONE outgoing has the same behaviour as INCOMING CALL
            return ChannelUtil.isMixableWith(mChannel, expectedChannel);
          } else {
            return (
              mChannel.channel.type === activeChannel.type &&
              mChannel.channel.direction === activeChannel.direction &&
              ChannelUtil.isMixableWith(mChannel, expectedChannel)
            );
          }
        }).length > 0
      );
    }

    let channelTypes = [
      ChannelType.MAIL,
      ChannelType.UNITARY_MAIL,
      ChannelType.PHONE,
      ChannelType.UNITARY_PHONE,
      ChannelType.VIDEO,
      ChannelType.CHAT,
      ChannelType.SOCIAL_NETWORK,
    ];
    for (let currentChannelType of channelTypes) {
      let channels = this.contactDeprecatedService.getChannelWithActiveContactsByChannelType(currentChannelType);
      if (channels.length > 0) {
        // tmp fix in case of current basket incoming accept mail outgoing
        return (
          channels.filter(
            c =>
              (c.type === ChannelType.BASKET && c.direction === ChannelDirection.INCOMING) ||
              isMixableWith(c, mixable, ChannelUtil.INCOMING_MAIL)
          ).length > 0
        );
      }
    }
    return true;
  }

  isFakeUnitMailKnowContact(responseId: string): boolean {
    return this.currentResponseId === responseId && this.mode !== 'EDIT';
  }

  linkRequestIdToContactEntry(responseId: string, esRequestId: number): IPromise<boolean> {
    return this.httpDeprecated
      .asPromise()
      .withData({})
      .post(CSR_FACADE_PREFIX + '/linkRequestIdToContactEntry', [responseId, esRequestId])
      .then(
        response => {
          if ('false' === response.data) {
            this.$q.reject('[outgoingMail] Error while linking RequestId To Booking Entry.');
            return false;
          } else {
            return 'true' === response.data;
          }
        },
        error => {
          this.$log.error('[outgoingMail] Error while linking RequestId To ContactEntry : ' + error);
          this.$q.reject(false);
          return false;
        }
      );
  }

  isNewCustomer(contactId: string): boolean {
    return NEW_CUSTOMER_ID === contactId;
  }

  private getFirstIasCustomerid(customer: Customer): Option<string> {
    let iasCustomerId = this.findExtraData(customer, 'iasCustomerId');
    if (iasCustomerId.isDefined()) {
      for (let id of iasCustomerId.get().split(',')) {
        if (id != null && id.length > 0) {
          return Option.of(id);
        }
      }
    }
    return None;
  }

  private findExtraData(contact: Customer, key: string): Option<string> {
    if (contact && contact.extraData) {
      const found = Object.keys(contact.extraData).find(_key => _key === key);
      return Option.of(found).flatMap(_key => Option.of(contact.extraData[_key]));
    }
    return None;
  }

  private loadContactByUuid(contactUuid: string, iasId: string | undefined, email: string): IPromise<boolean> {
    return this.searchService.searchContactByUUid(contactUuid).then(
      rawContact => {
        if (isDefined(rawContact)) {
          this.selectedRecipientType = RecipientSearchType.DIRECT;
          let recipientList = this.buildRecipientList([rawContact])
            .filter(recipient => this.isEmpty(iasId) || recipient.id === iasId)
            .filter(recipient => !isEmailValid(email) || recipient.email === email);
          if (recipientList.length === 1) {
            this.matchingDirectContacts = this.matchingContacts = recipientList;
            this.selectedDirectContact = this.selectedContact = this.matchingContacts[0];
            this.updateContact();
            return true;
          } else if (recipientList.length > 1) {
            this.matchingDirectContacts = this.matchingContacts = recipientList;
            this.selectedDirectContact = this.selectedContact = recipientList[0];
            return false;
          }
        }
        this.resetSelection();
        this.matchingDirectContacts = [];
        return false;
      },
      error => {
        this.$log.error(`Error while loading contact [${contactUuid}] : ${error}`);
        this.matchingDirectContacts = this.matchingContacts = [];
        this.selectedContact = undefined;
        this.resetSelection();
        return false;
      }
    );
  }

  private loadContactByEmail(email: string, autoOpen: boolean): IPromise<boolean> {
    this.selectedRecipientType = RecipientSearchType.DIRECT;
    return this.searchService.searchContactByEmail(email).then(
      rawContacts => {
        if (isDefined(rawContacts) && rawContacts.length > 0) {
          this.selectedRecipientType = RecipientSearchType.DIRECT;
          let recipientList = this.buildRecipientList(rawContacts).filter(
            recipient => (recipient.email || '').toLocaleLowerCase() === (email || '').toLowerCase()
          );
          if (recipientList.length === 1) {
            if (autoOpen) {
              this.matchingDirectContacts = this.matchingContacts = recipientList;
              this.selectedDirectContact = this.selectedContact = this.matchingContacts[0];
              this.updateContact();
              return true;
            }
          } else if (recipientList.length > 1) {
            this.matchingDirectContacts = this.matchingContacts = recipientList;
            this.selectedDirectContact = this.selectedContact = recipientList[0];
            return false;
          }
        }
        this.resetSelection();
        this.emailInput = email;
        this.performSearchContacts();
        return false;
      },
      error => {
        this.$log.error(`Error while loading contact [${email}] : ${error}`);
        this.matchingDirectContacts = [];
        this.resetSelection();
        return false;
      }
    );
  }

  private resetSelection(): void {
    let recipientTypes = this.getRecipientTypes();
    this.emailInput = '';
    this.selectedRecipientType = recipientTypes.length > 0 ? recipientTypes[0] : RecipientSearchType.DIRECT;
    this.mode = 'EDIT';
    this.selectedContact = undefined;
    this.selectedDirectContact = undefined;
    this.matchingContacts = [];
    this.buffer = [];
    this.iframeLoaded = false;
    this.lock = false;
    this.correspondantInput = '';
    this.candidateCorrespondants = [];
    this.selectedCorrespondant = undefined;
  }

  private canSendMail(): boolean {
    return this.sessionDeprecatedService.hasPermission('CSR::self:send_mail');
  }

  private canSendFreeMail(): boolean {
    return this.sessionDeprecatedService.hasPermission('CSR::outgoing_mail:free_mail');
  }

  private canSearchByContactId(): boolean {
    return this.sessionDeprecatedService.hasPermission('CSR::outgoing_mail:search_by_contact_id');
  }

  private canSearchInDirectory(): boolean {
    return this.sessionDeprecatedService.hasPermission('CSR::outgoing_mail:search_in_directory');
  }

  private loadEpticaRequest(responseId: string): IPromise<IEpticaOutgoingFrame> {
    return (this.requestPromise = this.contactDeprecatedService
      .getContact(responseId)
      .then(contact => {
        if (this.selectedContact && this.selectedContact.id != null) {
          return this.epticaDeprecatedService
            .loadOutgoingRequest({
              idType: 'ias',
              customerId: this.selectedContact.id,
              responseId,
              draftId: contact.requestDraftId,
            })
            .then(request => {
              request.setOutgoingClass();
              return request;
            });
        } else {
          this.toasterFacade.alert({ message: 'agent:toast.openOutgoingMailFailed' });
          this.$log.error(`the external id defined in the contact ${this.selectedContact && this.selectedContact.name}`);
          return this.$q.reject(`the external id defined in the contact ${this.selectedContact && this.selectedContact.name}`);
        }
      })
      .finally(() => (this.iframeLoaded = true)));
  }

  private createContactIfNecessary(newUri: string): IPromise<IRecipient | undefined> {
    if (!this.selectedContact) {
      let contactEmail: Option<string> = None;
      if (this.selectedRecipientType === RecipientSearchType.BY_EMAIL) {
        contactEmail = Option.of(this.emailInput);
      } else if (this.selectedRecipientType === RecipientSearchType.BY_DIRECTORY) {
        contactEmail = Option.of(this.selectedCorrespondant).map(correspondant => correspondant.email);
      }

      if (!contactEmail.filter(email => email !== '').isDefined()) {
        this.$log.warn('The email is not defined => Cannot sent unit mail');
        this.mode = 'EDIT';
        return this.$q.reject();
      }

      return this.createContact(this.emailInput).then(
        uuid => this.searchContactByUuidAsRecipient(uuid, contactEmail.get()),
        error => this.$q.reject(error)
      );
    } else {
      const email = newUri || '';
      const uuid = (this.selectedContact && this.selectedContact.uuid) || '';
      if (!this.selectedContact.uuid) {
        if (!email) {
          return this.$q.reject();
        }
        return this.createContact(email).then(
          _uuid => this.searchContactByUuidAsRecipient(_uuid, email),
          error => this.$q.reject(error)
        );
      }
      return this.customerCardsDeprecatedService
        .getCustomerCard(uuid)
        .then(customer => {
          if (!customer) throw new Error();
          return customer;
        })
        .then(
          customer => {
            // always resynchro the contact to prevent Eptica issue
            return this.updateContactWithNewAddress(customer, email, 2000).then(
              _uuid => this.searchContactByUuidAsRecipient((this.selectedContact && this.selectedContact.uuid) || '', email),
              error => this.$q.reject(error)
            );
          },
          error => this.$q.reject(error)
        );
    }
  }

  private searchContactByUuidAsRecipient(uuid: string, email: string): IPromise<IRecipient> {
    return this.withRetry(
      () => this.searchService.searchContactByUUid(uuid),
      rawContact => this.predicateContactCreated(rawContact),
      5,
      1000
    ).then(contact => this.contactToRecipient(contact, email));
  }

  private predicateContactCreated(rawContact: any): boolean {
    if (isUndefined(rawContact)) {
      return false;
    }
    for (let address of rawContact.addresses) {
      if (address.channel === 'MAIL_INCOMING' && (address.externalId || '') === '') {
        return false;
      }
    }
    return true;
  }

  private contactToRecipient(rawContact: any, email: string): IPromise<IRecipient> {
    for (let address of rawContact.addresses) {
      if (
        address.channel === 'MAIL_INCOMING' &&
        (address.externalId || '').length > 0 &&
        (email || '').toLowerCase() === (address.uri || '').toLowerCase()
      ) {
        return this.$q.resolve({
          contactId: rawContact.contactId,
          id: address.externalId,
          name: (rawContact.contactFirstName || '') + ' ' + (rawContact.contactLastName || '').toUpperCase(),
          uuid: rawContact.uuid || '',
          lastname: rawContact.contactLastName || '',
          firstname: rawContact.contactFirstName || '',
          email: address.uri,
        });
      }
    }
    return this.$q.reject();
  }

  private compare(a: any, b: any): Option<number> {
    // Checks the nulls ?
    if (a == null && b == null) {
      return Option.of(0);
    } else if (a == null) {
      return Option.of(-1);
    } else if (b == null) {
      return Option.of(+1);
    }

    // if primitive
    let type = typeof a;
    if (type !== 'object' && type !== 'function') {
      return Option.of(a === b ? 0 : a < b ? -1 : 1);
    }

    return None;
  }

  private compareRecipient(recipientA: IRecipient, recipientB: IRecipient): number {
    return this.compare(recipientA, recipientB)
      .orElseFunc(() => this.compare(recipientA.lastname, recipientB.lastname))
      .orElseFunc(() => this.compare(recipientA.firstname, recipientB.firstname))
      .orElseFunc(() => this.compare(recipientA.email, recipientB.email))
      .getOrElse(0);
  }

  private buildRecipientList(rawContacts: Array<any>, newMail?: string): Array<IRecipient> {
    const recipients: Array<IRecipient> = [];
    let adrAlreadyExist = false;

    // remove duplicates with extraData.iasCustomerId subfield
    rawContacts = rawContacts
      .map(val => {
        const [id] = !!val && !!val.extraData ? val.extraData.filter(({ key }) => key === 'iasCustomerId').map(({ value }) => value) : [-1];
        return id;
      })
      // store the keys of the unique objects
      .map((value, index, final) => (final.indexOf(value) === index ? index : -1))
      // eliminate the dead keys & store unique objects
      .filter(index => rawContacts[index])
      .map(index => rawContacts[index])
      .filter(value => {
        const isMail = value?.extraData?.some(({ key }) => key === 'iasCustomerId') ?? false;
        const hasUri = value?.addresses?.some(({ uri }) => !!uri) ?? false;
        return isMail && hasUri;
      });
    forEach(rawContacts, rawContact => {
      const addresses = rawContact.addresses.filter(({ uri }) => !!uri);
      forEach(addresses, address => {
        if (address.channel === 'MAIL_INCOMING' && (address.externalId || '').length > 0) {
          adrAlreadyExist = adrAlreadyExist || this.compare((address.uri || '').toLowerCase(), (newMail || '').toLowerCase()).get() === 0;
          const recipient: IRecipient = {
            contactId: rawContact.contactId,
            id: address.externalId,
            name: (rawContact.contactFirstName || '') + ' ' + (rawContact.contactLastName || '').toUpperCase(),
            uuid: rawContact.uuid || '',
            lastname: rawContact.contactLastName || '',
            firstname: rawContact.contactFirstName || '',
            email: address.uri,
          };
          if (this.matchInput(recipient)) {
            recipients.push(recipient);
          }
        }
      });
    });
    if (!adrAlreadyExist && !!newMail && isEmailValid(newMail)) {
      const newRecipient: IRecipient = {
        contactId: '',
        id: NEW_CUSTOMER_ID,
        name: '',
        uuid: '',
        lastname: '',
        firstname: '',
        email: `${newMail} (${this.$i18next.t('agent:contact.info.new_customer_card')})`,
      };
      recipients.unshift(newRecipient);
    }

    recipients.sort(this.compareRecipient.bind(this));
    this.selectedContact = recipients.length > 0 ? recipients[0] : undefined;
    return recipients;
  }

  private matchInput(recipient: IRecipient): boolean {
    switch (this.selectedRecipientType) {
      case RecipientSearchType.BY_EMAIL:
        return (
          recipient.name.toLowerCase().indexOf(this.emailInput.toLowerCase()) >= 0 ||
          recipient.email.toLowerCase().indexOf(this.emailInput.toLowerCase()) >= 0
        );
      case RecipientSearchType.BY_DIRECTORY:
        return (
          (recipient.email || '').toLowerCase() === ((this.selectedCorrespondant && this.selectedCorrespondant.email) || '').toLowerCase()
        );
      default:
        return true;
    }
  }

  private manageAutoCompleteQueue(promise: IPromise<Array<any>>): IPromise<Array<any>> {
    let defered = this.$q.defer<Array<any>>();
    if (this.buffer.length <= 1) {
      this.buffer[0] = promise;
    } else {
      this.buffer[1] = promise;
    }
    promise.then(results => {
      let index = this.buffer.indexOf(promise);
      if (index === this.buffer.length - 1) {
        defered.resolve(results);
        this.buffer = [];
      } else {
        this.buffer.splice(index, 1);
      }
    });
    return defered.promise;
  }

  /**
   * @returns A promise of an uuid, or "false"
   */
  private createContact(email: string): IPromise<string> {
    return this.httpDeprecated
      .asPromise()
      .withData(this.getselectedContactAsCustomerCardDto(email) || {})
      .post(CSR_FACADE_PREFIX + '/createClientFile')
      .then(
        response => ('false' === response.data ? this.$q.reject('[outgoingMail] Error while creating the contact.') : response.data),
        error => {
          this.$log.error('[outgoingMail] Error while creating the contact : ' + error);
          return this.$q.reject('[outgoingMail] Error while creating the contact : ' + error);
        }
      );
  }

  createContactEntry(data): IPromise<string> {
    return this.httpDeprecated
      .asPromise()
      .withData(data)
      .post(CSR_FACADE_PREFIX + '/createContactEntry')
      .then(
        response => ('false' === response.data ? this.$q.reject('[outgoingMail] Error while creating the contact entry.') : response.data),
        error => {
          this.$log.error('[outgoingMail] Error while creating the contact entry: ' + error);
          return this.$q.reject('[outgoingMail] Error while creating the contact entry: ' + error);
        }
      );
  }

  private updateContactWithNewAddress(customerData: CustomerData, newEmail: string, delay = 0): IPromise<string> {
    customerData.addresses.push({
      id: '',
      customerId: customerData.customer.id,
      uri: newEmail,
      channel: ChanEnum.In.Mail,
      externalId: undefined,
      channelType: undefined,
      firstname: undefined,
      lastname: undefined,
      displayname: undefined,
      avatarUrl: undefined,
    });
    // address not found --> update customcard with new adress
    const data: CustomerChangesData = {
      partial: true,
      customer: { id: customerData.customer.id, changes: customerData.customer },
      addresses: customerData.addresses.map(adr => ({ id: adr.id, changes: adr })),
    };
    return this.httpDeprecated
      .asPromise()
      .withData(this.toCustomerUpdateDto(data))
      .post(`${CSR_FACADE_PREFIX}/updateClientFile`)
      .then(
        response => ('false' === response.data ? this.$q.reject('[outgoingMail] Error while updating the contact.') :
          this.delay(delay).then(() => response.data)
        ),
        error => {
          this.$log.error('[outgoingMail] Error while updating the contact : ' + error);
          return this.$q.reject('[outgoingMail] Error while updating the contact : ' + error);
        }
      );
  }

  private toCustomerUpdateDto({
    customer,
    addresses,
  }: CustomerChangesData): Omit<ContactDto, 'customerRepositoryName' | 'blackListedCsr' | 'creationDate'> {
    const uuid = customer.id as string;
    const customerChanges = customer.changes;
    const addressChanges = addresses.map(({ changes }) => changes);
    return {
      uuid,
      firstname: customerChanges.firstname || '',
      lastname: customerChanges.lastname || '',
      companyname: customerChanges.companyname || '',
      contactId: customerChanges.externalId || '',
      description: customerChanges.description || '',
      assignedCsrId: customerChanges.assignedCsrId || '',
      civility: customerChanges.civility || Civility.NOTSET,
      addresses: addressChanges.map(address => CustomerService.toCustomerAddressDto(address)),
      consent: customerChanges.consent,
      extraData: customerChanges.extraData ? Object.entries(customerChanges.extraData).map(([key, value]) => ({ key, value })) : [],
    };
  }

  private getselectedContactAsCustomerCardDto(email: string): ContactDto | undefined {
    switch (this.selectedRecipientType) {
      case RecipientSearchType.BY_EMAIL:
        return {
          uuid: '',
          firstname: '',
          lastname: '',
          companyname: '',
          addresses: [
            {
              channel: 'MAIL_INCOMING',
              uri: email,
              avatarUrl: '',
              channelType: '',
              displayName: '',
              firstname: '',
              lastname: '',
              externalId: '',
            },
          ],
          contactId: '',
          assignedCsrId: '',
          description: '',
          civility: Civility.NOTSET,
          consent: false,
          blackListedCsr: [],
          creationDate: 0,
          customerRepositoryName: '',
          extraData: [],
        };
      case RecipientSearchType.BY_DIRECTORY:
        return {
          uuid: '',
          firstname: (this.selectedCorrespondant && this.selectedCorrespondant.firstname) || '',
          lastname: (this.selectedCorrespondant && this.selectedCorrespondant.lastname) || '',
          companyname: (this.selectedCorrespondant && this.selectedCorrespondant.companyname) || '',
          addresses: [
            {
              channel: 'MAIL_INCOMING',
              uri: (this.selectedCorrespondant && this.selectedCorrespondant.email) || '',
              avatarUrl: '',
              channelType: '',
              displayName: '',
              firstname: '',
              lastname: '',
              externalId: '',
            },
            {
              channel: 'PHONE_INCOMING',
              uri: (this.selectedCorrespondant && this.selectedCorrespondant.phoneNumber) || '',
              avatarUrl: '',
              channelType: '',
              displayName: '',
              firstname: '',
              lastname: '',
              externalId: '',
            },
          ],
          contactId: '',
          assignedCsrId: '',
          description: '',
          civility: Civility.NOTSET,
          consent: false,
          blackListedCsr: [],
          creationDate: 0,
          customerRepositoryName: '',
          extraData: [],
        };
      default:
        return undefined;
    }
  }

  private withRetry<T>(action: () => IPromise<T>, predicate: (T) => boolean, nbRetry: number, interval: number): IPromise<T> {
    return action().then(result => {
      if (predicate(result)) {
        return this.$q.resolve(result);
      } else if (nbRetry > 0) {
        return this.delay(interval).then(() => this.withRetry(action, predicate, nbRetry - 1, interval));
      } else {
        return this.$q.reject('Predcate not satified');
      }
    });
  }

  private delay(interval: number) {
    let defer = this.$q.defer();
    setTimeout(() => defer.resolve(), interval);
    return defer.promise;
  }

  private isEmpty(s: string | null | undefined): boolean {
    return !s || s.length <= 0;
  }
}

export default MODULE_NAME;
